import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Loader,
  toast,
} from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Loader2, Plus, Trash2, User } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Customer } from "@/server/platform/db/schema/crm/customer";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import type { PaymentInput } from "../../domain/types";
import type { PaymentMethod } from "../../domain/repo/get-payment-methods";
import { posApi } from "../api/client";
import {
  useFindOrCreateCustomerByPhone,
  usePaymentMethods,
  useValidateVoucher,
} from "../api/queries";
import {
  clearCustomerDisplayCartDiscount,
  setCustomerDisplayCartDiscount,
} from "../lib/customer-display-cart-discount";
import {
  buildCustomerDisplayPaymentProgress,
  clearCustomerDisplayPaymentProgress,
  setCustomerDisplayPaymentProgress,
} from "../lib/customer-display-payment-progress";
import { useSyncCustomerDisplayCustomer } from "../hooks/useSyncCustomerDisplayCustomer";
import {
  buildLoyaltyPreviewItemsFromPaymentOrderItems,
  excludeFreeProductRewardFromLoyaltyPreviewItems,
} from "../lib/loyalty-preview-items";
import {
  buildPaymentCouponSnapshots,
  buildPaymentVoucherSnapshots,
} from "../lib/offline-sync/local-pricing-snapshot";
import {
  getCachedEligibleCouponsPaged,
  hasCouponReferenceCache,
  saveCouponRuleToReferenceCache,
} from "../lib/offline-sync/reference-cache";
import { resolveLocalFirstCouponValidation } from "../lib/offline-sync/resolve-local-first-coupon";
import { resolveLocalFirstCustomerByPhone } from "../lib/offline-sync/resolve-local-first-customer";
import type {
  LocalOrderCouponSnapshot,
  LocalOrderCustomerSnapshot,
  LocalOrderVoucherSnapshot,
} from "../lib/offline-sync/types";
import { paymentUiLog } from "../lib/payment-ui-log";
import { LoyaltyPointsPreview } from "./LoyaltyPointsPreview";
import { LoyaltyRewardPicker } from "./LoyaltyRewardPicker";

type PaymentCouponListItem = {
  id: string;
  code: string;
  calculatedDiscount: number;
};

export type PaymentCouponPickResult = {
  code: string;
  calculatedDiscount: number;
  coupon?: {
    id?: string;
    discountType?: string;
    discountValue?: number | string | null;
  };
  validationSource: "network" | "cache";
  needsServerConfirmation: boolean;
};

type PaymentCouponComboboxProps = {
  branchId: string | null;
  customerId?: string | null;
  /** ຍອດສຳລັບຄິດສ່ວນຫຼຸດໃນລາຍການ lookup */
  orderSubtotalForList: number;
  /** ຍອດສຳລັບ validate ຕອນເລືອກ */
  baseAmountValidate: number;
  value: string;
  onPickResult: (result: PaymentCouponPickResult | null) => void;
  placeholder?: string;
  queryKeySuffix?: string;
};

function canUseNetwork(): boolean {
  return typeof navigator === "undefined" || navigator.onLine;
}

function PaymentCouponCombobox({
  branchId,
  customerId,
  orderSubtotalForList,
  baseAmountValidate,
  value,
  onPickResult,
  placeholder = "ເລືອກໂຄດສ່ວນຫຼຸດ...",
  queryKeySuffix = "order",
}: PaymentCouponComboboxProps) {
  const [couponCacheAvailable, setCouponCacheAvailable] = useState<
    boolean | null
  >(null);

  useEffect(() => {
    if (!branchId) {
      setCouponCacheAvailable(false);
      return;
    }
    void hasCouponReferenceCache(branchId).then(setCouponCacheAvailable);
  }, [branchId]);

  const queryKey = useMemo(
    () =>
      [
        "pos",
        "coupons",
        "eligible",
        "page",
        branchId ?? "",
        customerId ?? "",
        String(Math.round(orderSubtotalForList * 100) / 100),
        queryKeySuffix,
      ] as const,
    [branchId, customerId, orderSubtotalForList, queryKeySuffix],
  );

  const validateCouponSelection = async (
    code: string,
  ): Promise<PaymentCouponPickResult | null> => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed || !branchId) return null;

    const result = await resolveLocalFirstCouponValidation({
      branchId,
      code: trimmed,
      customerId: customerId ?? undefined,
      baseAmount: baseAmountValidate,
      validateOnline: () =>
        posApi.validateCoupon(
          trimmed,
          customerId ?? undefined,
          undefined,
          undefined,
          baseAmountValidate,
        ),
    });

    if (
      !result.valid ||
      result.calculatedDiscount == null ||
      result.coupon == null
    ) {
      return null;
    }

    return {
      code: result.code,
      calculatedDiscount: result.calculatedDiscount,
      coupon: {
        id: result.coupon.id,
        discountType: result.coupon.discountType,
        discountValue: result.coupon.discountValue,
      },
      validationSource: result.validationSource,
      needsServerConfirmation: result.needsServerConfirmation,
    };
  };

  const offlineWithoutCache =
    !canUseNetwork() && couponCacheAvailable === false;
  const disabledMessage = "ບໍ່ມີ coupon cache — ກະລຸນາເຊື່ອມຕໍ່ອິນເຕີເນັດ ຫຼື refresh cache";

  if (!branchId || offlineWithoutCache) {
    return (
      <div className="space-y-1">
        <Input
          value={value}
          placeholder={placeholder}
          disabled
          readOnly
          className="opacity-70"
        />
        <p className="text-muted-foreground text-xs">{disabledMessage}</p>
      </div>
    );
  }

  return (
    <InfiniteCombobox<PaymentCouponListItem>
      queryKey={[...queryKey]}
      queryFn={async ({ search, pageParam }) => {
        if (canUseNetwork()) {
          const r = await posApi.getEligibleCouponsPaged({
            customerId: customerId ?? undefined,
            orderSubtotal: orderSubtotalForList,
            search: search.trim() || undefined,
            page: pageParam,
            pageSize: 20,
          });
          if (branchId) {
            await Promise.all(
              r.coupons.map((coupon) =>
                saveCouponRuleToReferenceCache(branchId, coupon).catch(
                  () => undefined,
                ),
              ),
            );
          }
          return {
            items: r.coupons.map((coupon) => ({
              id: coupon.id,
              code: coupon.code,
              calculatedDiscount: coupon.calculatedDiscount,
            })),
            nextPage: r.nextPage,
          };
        }

        const cached = await getCachedEligibleCouponsPaged({
          branchId,
          customerId: customerId ?? undefined,
          orderSubtotal: orderSubtotalForList,
          search: search.trim() || undefined,
          page: pageParam,
          pageSize: 20,
        });
        return {
          items: cached.items.map((item) => ({
            id: item.id,
            code: item.code,
            calculatedDiscount: item.calculatedDiscount,
          })),
          nextPage: cached.nextPage,
        };
      }}
      preloadQueryFn={async (code) => {
        const c = code.trim().toUpperCase();
        if (!c) return null;
        const picked = await validateCouponSelection(c);
        if (!picked) return null;
        return {
          id: picked.coupon?.id ?? c,
          code: picked.code,
          calculatedDiscount: picked.calculatedDiscount,
        };
      }}
      getLabel={(couponRow) =>
        `${couponRow.code} (−${couponRow.calculatedDiscount.toLocaleString("lo-LA")} ₭)`
      }
      getValue={(couponRow) => couponRow.code}
      value={value}
      onValueChange={(code) => {
        void (async () => {
          const trimmed = code.trim().toUpperCase();
          if (!trimmed) {
            onPickResult(null);
            return;
          }
          try {
            const picked = await validateCouponSelection(trimmed);
            if (picked) {
              onPickResult(picked);
            } else {
              onPickResult(null);
              toast.error("ໂຄດສ່ວນຫຼຸດບໍ່ຖືກຕ້ອງ");
            }
          } catch (error) {
            onPickResult(null);
            toast.error(
              error instanceof Error
                ? error.message
                : "ເກີດຂໍ້ຜິດພາດໃນການກວດສອບໂຄດສ່ວນຫຼຸດ",
            );
          }
        })();
      }}
      placeholder={placeholder}
      clearable
    />
  );
}

export type PaymentDialogOrderItem = {
  id: string;
  productName: string;
  lineSubtotal: number;
  /** ภาษีของรายการ (ใช้รวมกับ lineSubtotal เป็นฐานคำนวณส่วนลด) */
  taxTotal?: number;
  variantId?: string | null;
  quantity?: number;
};

export type PaymentDialogCouponApplication = {
  code: string;
  orderItemIds: string[];
};

/** ຂໍ້ມູນລູກຄ້າເບື້ອງຕົ້ນຈາກ parent (ສະແດງຊື່/ເບີ) */
export type PaymentDialogInitialCustomer = Pick<
  Customer,
  "id" | "firstName" | "lastName" | "phone"
> & {
  localCustomerId?: string;
  source?: LocalOrderCustomerSnapshot["source"];
  capturedAt?: string;
};

export type PaymentDialogCustomerSelection = PaymentDialogInitialCustomer & {
  customerSnapshot?: LocalOrderCustomerSnapshot;
};

import type { LocalOrderRewardSnapshot } from "../lib/offline-sync/types";

export type PaymentDialogCompletePricing = {
  paymentCouponSnapshots: LocalOrderCouponSnapshot[];
  paymentVoucherSnapshots: LocalOrderVoucherSnapshot[];
  rewardId?: string;
  rewardSnapshot?: LocalOrderRewardSnapshot;
};

type CouponApplyMode = "order" | "per_line";

const MAX_PAYMENT_ROWS = 8;

function getCustomerSourceLabel(
  source?: LocalOrderCustomerSnapshot["source"],
): string {
  switch (source) {
    case "server":
      return "server customer";
    case "cache":
      return "cached customer";
    case "local_draft":
      return "local draft";
    default:
      return "customer";
  }
}

/** ກັນ Radix Dialog ປິດເມື່ອຄລິກ portal ຂອງ Select / Popover / Combobox ພາຍໃນ dialog */
function shouldPreventPaymentDialogDismiss(event: Event): boolean {
  const target = event.target;
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest("[data-radix-popper-content-wrapper]") ||
      target.closest("[data-radix-select-content]") ||
      target.closest("[data-radix-popover-content]") ||
      target.closest('[role="listbox"]') ||
      target.closest('[role="menu"]') ||
      target.closest("[cmdk-root]") ||
      target.closest("[data-sonner-toast]") ||
      target.closest("[data-sonner-toaster]"),
  );
}

function makePaymentRowId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
}

function formatAmountInput(rounded: number): string {
  if (rounded === 0) return "0";
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

function parseAmountField(raw: string): number {
  const n = Number.parseFloat(raw.replace(/[\s,]/g, "") || "0");
  return Math.round(n * 100) / 100;
}

type PaymentRow = { id: string; methodId: string; amount: string };

type PaymentDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderTotal: number;
  onComplete: (
    payments: PaymentInput[],
    voucherCodes?: string[],
    couponCode?: string,
    couponOrderItemIds?: string[],
    couponApplications?: PaymentDialogCouponApplication[],
    customerId?: string,
    customerSelection?: PaymentDialogCustomerSelection,
    rewardId?: string,
    paymentPricing?: PaymentDialogCompletePricing,
  ) => void | Promise<void>;
  /** ອໍເດີ server (OPEN) — ຈຳເປັນສຳລັບແລກລາງວັນອອນໄລນໃນ Payment */
  orderId?: string | null;
  /** ปิดปุ่มยืนยันระหว่าง parent กำลัง checkout/complete */
  isSubmitting?: boolean;
  branchId: string | null;
  /** ລູກຄ້າຈາກ parent (ອໍເດີ / ສະຫຼຸບ order) — ໃຊ້ເປັນຄ່າເລີ່ມ */
  customerId?: string | null;
  initialCustomer?: PaymentDialogInitialCustomer | null;
  allowLocalCustomerDraft?: boolean;
  deviceId?: string | null;
  voucherSectionDefaultOpen?: boolean;
  orderItems?: PaymentDialogOrderItem[];
  /** ยอดฐานรวมภาษีของออเดอร์ (subtotal+tax) เมื่อไม่มี orderItems ใช้สำหรับ validate coupon */
  orderBaseInclTax?: number;
};

export function PaymentDialog({
  open,
  onOpenChange,
  orderTotal,
  onComplete,
  isSubmitting = false,
  branchId,
  customerId: customerIdProp,
  initialCustomer,
  allowLocalCustomerDraft = false,
  deviceId,
  voucherSectionDefaultOpen = true,
  orderItems,
  orderBaseInclTax,
  orderId,
}: PaymentDialogProps) {
  const submitLockRef = useRef(false);
  const queryClient = useQueryClient();
  const { data: paymentMethods, isLoading: isLoadingMethods } =
    usePaymentMethods(branchId);
  const findOrCreateCustomerMutation = useFindOrCreateCustomerByPhone();
  const [customerPhone, setCustomerPhone] = useState("");
  const [selectedCustomer, setSelectedCustomer] =
    useState<PaymentDialogCustomerSelection | null>(null);
  const paymentCustomerWasOpenRef = useRef(false);
  /**
   * Radix nested overlay: ປິດ Select/Popover ພາຍໃນ ຫຼື ຫຼັງຄົ້ນຫາລູກຄ້າ
   * → Dialog ພາຫູ່ມັກສົ່ງ onOpenChange(false) ແລ້ວ handleClose ລ້າງ state
   */
  const suppressDismissRef = useRef(false);
  const prevEffectiveCustomerIdRef = useRef<string | undefined>(undefined);

  const runWithDismissSuppressed = async (fn: () => Promise<void>) => {
    suppressDismissRef.current = true;
    try {
      await fn();
    } finally {
      window.setTimeout(() => {
        suppressDismissRef.current = false;
      }, 450);
    }
  };

  const effectiveCustomerId =
    selectedCustomer?.id ||
    selectedCustomer?.customerSnapshot?.customerId ||
    customerIdProp ||
    undefined;

  useSyncCustomerDisplayCustomer({
    enabled: open,
    branchId,
    customer: selectedCustomer,
  });

  const [paymentRows, setPaymentRows] = useState<PaymentRow[]>(() => [
    { id: makePaymentRowId(), methodId: "", amount: "" },
  ]);
  const paymentDialogWasOpenRef = useRef(false);
  const [voucherCode, setVoucherCode] = useState("");
  const [appliedVouchers, setAppliedVouchers] = useState<
    Array<{
      code: string;
      value: number;
    }>
  >([]);
  const [couponApplyMode, setCouponApplyMode] =
    useState<CouponApplyMode>("order");
  const [orderCouponPickerValue, setOrderCouponPickerValue] = useState("");
  const [validatedCoupon, setValidatedCoupon] =
    useState<PaymentCouponPickResult | null>(null);
  /** โหมดรายบรรทัด: orderItemId → ส่วนลดที่ validate แล้ว */
  const [lineCoupons, setLineCoupons] = useState<
    Record<string, PaymentCouponPickResult>
  >({});
  const [lineCouponEditingId, setLineCouponEditingId] = useState<string | null>(
    null,
  );
  const [lineEditPickerValue, setLineEditPickerValue] = useState("");
  const [selectedRewardId, setSelectedRewardId] = useState<string | null>(null);
  const [rewardPreview, setRewardPreview] = useState<{
    discountPreview?: number;
    eligible: boolean;
    snapshot?: LocalOrderRewardSnapshot;
  } | null>(null);

  const validateVoucherMutation = useValidateVoucher();

  const hasLineItems = Boolean(orderItems && orderItems.length > 0);

  // Calculate total voucher discount
  const totalVoucherDiscount = appliedVouchers.reduce(
    (sum, v) => sum + v.value,
    0,
  );

  const couponDiscountOrder = validatedCoupon?.calculatedDiscount ?? 0;
  const couponDiscountLines = Object.values(lineCoupons).reduce(
    (s, v) => s + v.calculatedDiscount,
    0,
  );
  const couponDiscount =
    couponApplyMode === "order" ? couponDiscountOrder : couponDiscountLines;

  const rewardDiscount =
    selectedRewardId && rewardPreview?.eligible
      ? (rewardPreview.discountPreview ?? 0)
      : 0;

  // Calculate effective order total (after vouchers, coupon, and reward)
  const effectiveOrderTotal = Math.max(
    0,
    orderTotal - totalVoucherDiscount - couponDiscount - rewardDiscount,
  );

  // เปิด dialog → รีเซ็ตเป็น 1 แถว + เติมยอด; ยังเปิดอยู่และเหลือ 1 แถว → sync ยอดตามส่วนลด
  useEffect(() => {
    if (!open) {
      paymentDialogWasOpenRef.current = false;
      return;
    }

    const rounded = Math.round(effectiveOrderTotal * 100) / 100;
    const amountStr = formatAmountInput(rounded);

    if (!paymentDialogWasOpenRef.current) {
      paymentDialogWasOpenRef.current = true;
      setPaymentRows([
        { id: makePaymentRowId(), methodId: "", amount: amountStr },
      ]);
      return;
    }

    setPaymentRows((prev) => {
      if (prev.length === 1) {
        const only = prev[0];
        if (!only) return prev;
        return [{ ...only, amount: amountStr }];
      }
      return prev;
    });
  }, [open, effectiveOrderTotal]);

  const rowAmounts = paymentRows.map((r) => parseAmountField(r.amount));
  const paid = rowAmounts.reduce((s, a) => s + a, 0);
  const paidRounded = Math.round(paid * 100) / 100;
  const effectiveRounded = Math.round(effectiveOrderTotal * 100) / 100;
  const change =
    paidRounded >= effectiveRounded ? paidRounded - effectiveRounded : 0;
  const remainingDue = Math.max(0, effectiveRounded - paidRounded);

  const rowMissingMethodForAmount = paymentRows.some(
    (r, i) => (rowAmounts[i] ?? 0) > 0 && !r.methodId,
  );
  const canComplete =
    !rowMissingMethodForAmount &&
    (effectiveRounded <= 0
      ? paymentRows.some((r) => r.methodId)
      : paidRounded >= effectiveRounded &&
        paymentRows.some((r, i) => (rowAmounts[i] ?? 0) > 0 && r.methodId));

  // Sync coupon/voucher/reward discount to Customer Display
  useEffect(() => {
    if (!open) {
      clearCustomerDisplayCartDiscount();
      return;
    }
    const totalDiscount =
      couponDiscount + totalVoucherDiscount + rewardDiscount;
    if (totalDiscount <= 0) {
      clearCustomerDisplayCartDiscount();
      return;
    }
    const discounts: Array<{ label: string; amount: number }> = [];
    if (couponApplyMode === "order" && validatedCoupon) {
      if (validatedCoupon.calculatedDiscount > 0) {
        discounts.push({
          label: `ໂຄດສ່ວນຫຼຸດ: ${validatedCoupon.code}`,
          amount: validatedCoupon.calculatedDiscount,
        });
      }
    } else if (couponApplyMode === "per_line" && orderItems) {
      for (const item of orderItems) {
        const lc = lineCoupons[item.id];
        if (lc && lc.calculatedDiscount > 0) {
          discounts.push({
            label: `${item.productName} — ${lc.code}`,
            amount: lc.calculatedDiscount,
          });
        }
      }
    }
    for (const v of appliedVouchers) {
      discounts.push({ label: `Voucher: ${v.code}`, amount: v.value });
    }
    if (rewardDiscount > 0) {
      const rewardName = rewardPreview?.snapshot?.rewardName;
      discounts.push({
        label: rewardName ? `ແລກລາງວັນ: ${rewardName}` : "ແລກຄະແນນ",
        amount: rewardDiscount,
      });
    }
    setCustomerDisplayCartDiscount({ discountTotal: totalDiscount, discounts });
    return () => clearCustomerDisplayCartDiscount();
  }, [
    open,
    couponDiscount,
    totalVoucherDiscount,
    rewardDiscount,
    rewardPreview?.snapshot?.rewardName,
    couponApplyMode,
    validatedCoupon,
    lineCoupons,
    orderItems,
    appliedVouchers,
  ]);

  // Sync split payment progress to Customer Display
  useEffect(() => {
    if (!open) {
      clearCustomerDisplayPaymentProgress();
      return;
    }

    const progress = buildCustomerDisplayPaymentProgress({
      paymentRows: paymentRows.map((row) => ({
        id: row.id,
        methodId: row.methodId,
        amount: parseAmountField(row.amount),
      })),
      paymentMethods: (paymentMethods ?? []).map((method: PaymentMethod) => ({
        id: method.id,
        displayName: method.displayName,
        type: method.type,
      })),
      grandTotal: effectiveRounded,
      paidTotal: paidRounded,
      remainingDue,
      change,
    });
    setCustomerDisplayPaymentProgress(progress);
    return () => clearCustomerDisplayPaymentProgress();
  }, [
    open,
    paymentRows,
    paymentMethods,
    effectiveRounded,
    paidRounded,
    remainingDue,
    change,
  ]);

  const resetCouponState = () => {
    setOrderCouponPickerValue("");
    setValidatedCoupon(null);
    setLineCoupons({});
    setLineCouponEditingId(null);
    setLineEditPickerValue("");
  };

  const resetPaymentCustomerState = () => {
    setCustomerPhone("");
    setSelectedCustomer(null);
  };

  const handleFindOrCreateCustomer = () =>
    void runWithDismissSuppressed(async () => {
      const trimmedPhone = customerPhone.trim();
      if (!trimmedPhone) {
        toast.error("ກະລຸນາປ້ອນເບີໂທລະສັບ");
        return;
      }
      if (!branchId) {
        toast.error("ບໍ່ພົບ Branch ID");
        return;
      }

      const resolved = await resolveLocalFirstCustomerByPhone({
        branchId,
        deviceId: deviceId ?? undefined,
        phone: trimmedPhone,
        findOnline: () =>
          findOrCreateCustomerMutation.mutateAsync(trimmedPhone),
        allowLocalDraft: allowLocalCustomerDraft,
      });

      if (!resolved) {
        setSelectedCustomer(null);
        toast.error("ເກີດຂໍ້ຜິດພາດໃນການຄົ້ນຫາລູກຄ້າ");
        return;
      }

      resetCouponState();
      setAppliedVouchers([]);
      queryClient.removeQueries({ queryKey: ["pos", "coupons", "eligible"] });
      setSelectedCustomer(resolved.selection);

      if (resolved.source === "server") {
        toast.success("ພົບລູກຄ້າສຳເລັດ");
        return;
      }
      if (resolved.source === "cache") {
        toast.success("ພົບລູກຄ້າຈາກ cache");
        return;
      }
      toast.success("ບັນທຶກລູກຄ້າໄວ້ໃນ local draft ແລ້ວ");
    });

  const initialCustomerSyncKey = initialCustomer
    ? [
        initialCustomer.id ?? "",
        initialCustomer.localCustomerId ?? "",
        initialCustomer.phone ?? "",
        initialCustomer.capturedAt ?? "",
      ].join("|")
    : "";

  useEffect(() => {
    if (!open) {
      paymentCustomerWasOpenRef.current = false;
      return;
    }
    if (!paymentCustomerWasOpenRef.current) {
      paymentCustomerWasOpenRef.current = true;
      if (initialCustomer) {
        const capturedAt =
          initialCustomer.capturedAt ?? new Date().toISOString();
        setSelectedCustomer({
          ...initialCustomer,
          capturedAt,
          customerSnapshot: {
            customerId: initialCustomer.id || undefined,
            localCustomerId: initialCustomer.localCustomerId,
            phone: initialCustomer.phone ?? undefined,
            firstName: initialCustomer.firstName ?? undefined,
            lastName: initialCustomer.lastName ?? undefined,
            source: initialCustomer.source ?? "server",
            capturedAt,
          },
        });
        setCustomerPhone(initialCustomer.phone?.trim() ?? "");
      } else {
        resetPaymentCustomerState();
      }
    }
  }, [open, initialCustomerSyncKey]);

  useEffect(() => {
    if (!open) {
      prevEffectiveCustomerIdRef.current = undefined;
      return;
    }
    const prev = prevEffectiveCustomerIdRef.current;
    prevEffectiveCustomerIdRef.current = effectiveCustomerId;
    if (!prev && effectiveCustomerId) {
      suppressDismissRef.current = true;
      const timer = window.setTimeout(() => {
        suppressDismissRef.current = false;
      }, 450);
      return () => window.clearTimeout(timer);
    }
  }, [open, effectiveCustomerId]);

  const handlePaymentDialogOpenChange = (nextOpen: boolean) => {
    if (nextOpen) return;
    if (suppressDismissRef.current) return;
    handleClose();
  };

  const handleRewardPreviewChange = useCallback(
    (
      p: {
        eligible: boolean;
        discountPreview?: number;
        snapshot?: LocalOrderRewardSnapshot;
      } | null,
    ) => {
      const next = p
        ? {
            eligible: p.eligible,
            discountPreview: p.discountPreview,
            snapshot: p.snapshot,
          }
        : null;
      setRewardPreview((prev) => {
        if (
          prev?.eligible === next?.eligible &&
          prev?.discountPreview === next?.discountPreview &&
          prev?.snapshot === next?.snapshot
        ) {
          return prev;
        }
        return next;
      });
    },
    [],
  );

  const handleCouponModeChange = (mode: CouponApplyMode) => {
    setCouponApplyMode(mode);
    resetCouponState();
  };

  const handleValidateVoucher = async () => {
    const trimmedCode = voucherCode.trim().toUpperCase();
    if (!trimmedCode) {
      toast.error("ກະລຸນາປ້ອນໂຄດ voucher");
      return;
    }

    // Check for duplicate
    if (appliedVouchers.some((v) => v.code === trimmedCode)) {
      toast.error("Voucher ນີ້ຖືກໃຊ້ແລ້ວ");
      return;
    }

    try {
      const result = await validateVoucherMutation.mutateAsync({
        code: trimmedCode,
        customerId: effectiveCustomerId,
      });

      if (result.valid && result.voucher) {
        setAppliedVouchers([
          ...appliedVouchers,
          {
            code: trimmedCode,
            value: Number(result.voucher.value),
          },
        ]);
        setVoucherCode("");
        toast.success("Voucher ຖືກຕ້ອງ");
      } else {
        toast.error(result.error || "Voucher ບໍ່ຖືກຕ້ອງ");
      }
    } catch (error) {
      console.error("Failed to validate voucher:", error);
      toast.error(
        error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການກວດສອບ voucher",
      );
    }
  };

  const handleRemoveVoucher = (code: string) => {
    setAppliedVouchers(appliedVouchers.filter((v) => v.code !== code));
  };

  const baseAmountOrderCoupon =
    orderItems && orderItems.length > 0
      ? orderItems.reduce((s, i) => s + i.lineSubtotal + (i.taxTotal ?? 0), 0)
      : (orderBaseInclTax ?? orderTotal);

  const rewardValidationItems = useMemo(
    () => buildLoyaltyPreviewItemsFromPaymentOrderItems(orderItems),
    [orderItems],
  );

  const loyaltyPreviewItems = useMemo(
    () =>
      excludeFreeProductRewardFromLoyaltyPreviewItems(
        rewardValidationItems,
        rewardPreview?.eligible ? rewardPreview.snapshot : null,
      ),
    [rewardValidationItems, rewardPreview?.eligible, rewardPreview?.snapshot],
  );

  const handleRemoveLineCoupon = (itemId: string) => {
    setLineCoupons((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
    if (lineCouponEditingId === itemId) {
      setLineCouponEditingId(null);
      setLineEditPickerValue("");
    }
  };

  const addPaymentRow = () => {
    setPaymentRows((rows) => {
      if (rows.length >= MAX_PAYMENT_ROWS) {
        toast.error(`ສູງສຸດ ${MAX_PAYMENT_ROWS} ຊ່ອງຊຳລະຕໍ່ບິນ`);
        return rows;
      }
      return [...rows, { id: makePaymentRowId(), methodId: "", amount: "" }];
    });
  };

  const removePaymentRow = (rowId: string) => {
    setPaymentRows((rows) => {
      if (rows.length <= 1) return rows;
      return rows.filter((r) => r.id !== rowId);
    });
  };

  const handleComplete = async () => {
    if (isSubmitting || submitLockRef.current) {
      return;
    }
    if (!canComplete) {
      if (rowMissingMethodForAmount) {
        toast.error("ກະລຸນາເລືອກວິທີຊຳລະໃຫ້ທຸກແຖວທີ່ມີຈຳນວນເງິນ");
        return;
      }
      if (effectiveRounded <= 0) {
        toast.error("ກະລຸນາເລືອກວິທີຊຳລະ");
        return;
      }
      toast.error("ຈຳນວນເງິນຈ່າຍບໍ່ພຽງພໍ");
      return;
    }

    let paymentInputs: PaymentInput[] = paymentRows
      .map((r, i) => ({
        methodId: r.methodId,
        amountGross: rowAmounts[i] ?? 0,
      }))
      .filter((p) => p.amountGross > 0);

    if (paymentInputs.length === 0 && effectiveRounded <= 0) {
      const firstWithMethod = paymentRows.find((r) => r.methodId);
      if (firstWithMethod) {
        paymentInputs = [
          { methodId: firstWithMethod.methodId, amountGross: 0 },
        ];
      }
    }

    const voucherCodes = appliedVouchers.map((v) => v.code);
    if (
      selectedRewardId &&
      (!rewardPreview?.eligible || rewardPreview == null)
    ) {
      toast.error("ກະລຸນາເລືອກລາງວັນທີ່ແລກໄດ້ ຫຼື ລຶບລາງວັນອອກ");
      return;
    }

    const paymentPricing: PaymentDialogCompletePricing = {
      paymentCouponSnapshots: buildPaymentCouponSnapshots({
        couponApplyMode,
        validatedCoupon,
        lineCoupons,
        orderItems,
        baseAmountOrderCoupon,
      }),
      paymentVoucherSnapshots: buildPaymentVoucherSnapshots(appliedVouchers),
      rewardId: selectedRewardId ?? undefined,
      rewardSnapshot: rewardPreview?.snapshot,
    };

    submitLockRef.current = true;
    paymentUiLog.info("submit start", {
      orderTotal,
      effectiveRounded,
      paymentRowCount: paymentInputs.length,
      hasCustomer: Boolean(effectiveCustomerId),
      hasReward: Boolean(selectedRewardId),
      voucherCount: voucherCodes.length,
    });

    // ປິດ dialog ທັນທີ — ຢ່າລໍ onComplete/print ຈົນຄ້າງ overlay
    onOpenChange(false);

    try {
      if (couponApplyMode === "per_line" && hasLineItems) {
        const couponApplications: PaymentDialogCouponApplication[] =
          Object.entries(lineCoupons).map(([orderItemId, v]) => ({
            code: v.code,
            orderItemIds: [orderItemId],
          }));
        await onComplete(
          paymentInputs,
          voucherCodes.length > 0 ? voucherCodes : undefined,
          undefined,
          undefined,
          couponApplications.length > 0 ? couponApplications : undefined,
          effectiveCustomerId,
          selectedCustomer ?? undefined,
          selectedRewardId ?? undefined,
          paymentPricing,
        );
      } else {
        const couponCodeToSend = validatedCoupon
          ? validatedCoupon.code
          : undefined;
        await onComplete(
          paymentInputs,
          voucherCodes.length > 0 ? voucherCodes : undefined,
          couponCodeToSend,
          undefined,
          undefined,
          effectiveCustomerId,
          selectedCustomer ?? undefined,
          selectedRewardId ?? undefined,
          paymentPricing,
        );
      }
      paymentUiLog.info("submit success");
    } catch (error) {
      paymentUiLog.error("submit failed", {
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      toast.error(
        error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການຈ່າຍເງິນ",
      );
    } finally {
      submitLockRef.current = false;
    }
  };

  const handleClose = () => {
    setPaymentRows([{ id: makePaymentRowId(), methodId: "", amount: "" }]);
    setVoucherCode("");
    setAppliedVouchers([]);
    resetCouponState();
    resetPaymentCustomerState();
    setSelectedRewardId(null);
    setRewardPreview(null);
    setCouponApplyMode("order");
    paymentCustomerWasOpenRef.current = false;
    onOpenChange(false);
  };

  const preventOverlayDismiss = (event: Event) => {
    if (shouldPreventPaymentDialogDismiss(event)) {
      event.preventDefault();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handlePaymentDialogOpenChange}>
      <DialogContent
        className="flex max-h-[92dvh] w-[calc(100vw-1.5rem)] max-w-5xl! flex-col gap-0 overflow-hidden p-6 sm:p-8"
        onInteractOutside={preventOverlayDismiss}
        onPointerDownOutside={preventOverlayDismiss}
        onFocusOutside={preventOverlayDismiss}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>ຈ່າຍເງິນ</DialogTitle>
          <DialogDescription>
            ລວມທັງໝົດ: {orderTotal.toLocaleString("lo-LA")} ₭
            {totalVoucherDiscount > 0 && (
              <span className="text-green-600">
                {" "}
                (ຫຼຸດ {totalVoucherDiscount.toLocaleString("lo-LA")} ₭)
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-4 [-webkit-overflow-scrolling:touch]">
          <div className="grid min-h-0 grid-cols-1 gap-6 sm:grid-cols-2">
            {/* ซ้าย: ລູກຄ້າ + ส่วนลด Coupon + Voucher */}
            <div className="flex min-h-0 flex-col gap-4 border-border/60 sm:border-r sm:pr-6">
              <div className="space-y-2 border-border/60 border-b pb-4">
                <Label htmlFor="payment-customer-phone" className="text-sm">
                  ເບີໂທລະສັບລູກຄ້າ (ບໍ່ບັງຄັບ)
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="payment-customer-phone"
                    type="tel"
                    placeholder="ປ້ອນເບີໂທລະສັບ"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        e.stopPropagation();
                        void handleFindOrCreateCustomer();
                      }
                    }}
                    disabled={findOrCreateCustomerMutation.isPending}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={() => void handleFindOrCreateCustomer()}
                    disabled={
                      findOrCreateCustomerMutation.isPending ||
                      !customerPhone.trim()
                    }
                    variant="outline"
                  >
                    {findOrCreateCustomerMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ກຳລັງຄົ້ນຫາ...
                      </>
                    ) : (
                      "ຄົ້ນຫາ"
                    )}
                  </Button>
                </div>
                {selectedCustomer && (
                  <div className="flex flex-wrap items-center gap-2 rounded-md bg-muted/50 p-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground text-sm">
                      ລູກຄ້າ: {selectedCustomer.firstName}{" "}
                      {selectedCustomer.lastName}
                      {selectedCustomer.phone
                        ? ` (${selectedCustomer.phone})`
                        : ""}
                    </span>
                    <span className="rounded-full border px-2 py-0.5 text-[11px] text-muted-foreground">
                      {getCustomerSourceLabel(selectedCustomer.source)}
                    </span>
                  </div>
                )}
                <LoyaltyPointsPreview
                  customerId={effectiveCustomerId}
                  items={loyaltyPreviewItems}
                  enabled={open}
                />
                <LoyaltyRewardPicker
                  branchId={branchId}
                  customerId={effectiveCustomerId}
                  orderId={orderId}
                  cartItems={rewardValidationItems}
                  orderGrandTotal={baseAmountOrderCoupon}
                  enabled={open}
                  value={selectedRewardId}
                  onChange={setSelectedRewardId}
                  onPreviewChange={handleRewardPreviewChange}
                />
              </div>

              <Collapsible
                defaultOpen={voucherSectionDefaultOpen}
                className="border-border/60 border-b pb-4"
              >
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="group flex w-full items-center justify-between rounded-md py-2 text-left font-medium text-sm hover:bg-muted/50"
                  >
                    <span>ໂຄດສ່ວນຫຼຸດ (Coupon)</span>
                    <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="flex flex-col gap-3 pt-2">
                  {hasLineItems && (
                    <div
                      className="flex flex-col gap-2"
                      role="radiogroup"
                      aria-label="ວິທີໃຊ້ coupon"
                    >
                      <div className="flex gap-1.5">
                        <label
                          className={cn(
                            "inline-flex w-full cursor-pointer items-center justify-center rounded-md border px-3 py-2 font-medium text-xs transition-colors",
                            couponApplyMode === "order"
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border/70 bg-background text-foreground hover:bg-muted/60",
                          )}
                          title="ສ່ວນຫຼຸດຄິດຈາກຍອດລວມບິນ"
                        >
                          <input
                            type="radio"
                            name="coupon-apply-mode"
                            checked={couponApplyMode === "order"}
                            onChange={() => handleCouponModeChange("order")}
                            className="sr-only"
                          />
                          ທັງອໍເດີ
                        </label>
                        <label
                          className={cn(
                            "inline-flex w-full cursor-pointer items-center justify-center rounded-md border px-3 py-2 font-medium text-xs transition-colors",
                            couponApplyMode === "per_line"
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border/70 bg-background text-foreground hover:bg-muted/60",
                          )}
                          title="ແຕ່ລະລາຍການໃສ່ໂຄດແຍກກັນ — ຫຼຸດເຉພາະລາຍການນັ້ນ"
                        >
                          <input
                            type="radio"
                            name="coupon-apply-mode"
                            checked={couponApplyMode === "per_line"}
                            onChange={() => handleCouponModeChange("per_line")}
                            className="sr-only"
                          />
                          ຕໍ່ລາຍການ
                        </label>
                      </div>
                    </div>
                  )}

                  {couponApplyMode === "order" && (
                    <div className="space-y-2">
                      <PaymentCouponCombobox
                        branchId={branchId}
                        customerId={effectiveCustomerId}
                        orderSubtotalForList={baseAmountOrderCoupon}
                        baseAmountValidate={baseAmountOrderCoupon}
                        value={orderCouponPickerValue}
                        onPickResult={(r) => {
                          if (!r) {
                            setOrderCouponPickerValue("");
                            setValidatedCoupon(null);
                            return;
                          }
                          setOrderCouponPickerValue(r.code);
                          setValidatedCoupon(r);
                          toast.success("ໂຄດສ່ວນຫຼຸດຖືກຕ້ອງ");
                        }}
                        placeholder="ຄົ້ນຫາ ຫຼື ເລືອກໂຄດສ່ວນຫຼຸດ..."
                      />
                      {validatedCoupon?.validationSource === "cache" && (
                        <div className="flex flex-wrap gap-1.5">
                          <Badge variant="outline" className="text-xs">
                            validated from cache
                          </Badge>
                          {validatedCoupon.needsServerConfirmation && (
                            <Badge variant="secondary" className="text-xs">
                              needs server confirmation
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {couponApplyMode === "per_line" && hasLineItems && (
                    <div className="max-h-52 space-y-2 overflow-y-auto rounded border border-border/60 p-2">
                      {orderItems?.map((item) => {
                        const applied = lineCoupons[item.id];
                        const isEditing = lineCouponEditingId === item.id;
                        return (
                          <div
                            key={item.id}
                            className="rounded-md border border-border/40 bg-muted/30 p-2 text-sm"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="truncate font-medium">
                                  {item.productName}
                                </div>
                                <div className="text-muted-foreground text-xs tabular-nums">
                                  {(
                                    item.lineSubtotal + (item.taxTotal ?? 0)
                                  ).toLocaleString("lo-LA")}{" "}
                                  ₭
                                </div>
                              </div>
                              {applied ? (
                                <div className="shrink-0 text-right">
                                  <div className="font-medium text-green-700 text-xs">
                                    {applied.code} (−
                                    {applied.calculatedDiscount.toLocaleString(
                                      "lo-LA",
                                    )}{" "}
                                    ₭)
                                  </div>
                                  {applied.validationSource === "cache" && (
                                    <div className="mt-1 flex flex-wrap justify-end gap-1">
                                      <Badge
                                        variant="outline"
                                        className="text-[10px]"
                                      >
                                        cache
                                      </Badge>
                                    </div>
                                  )}
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={() =>
                                      handleRemoveLineCoupon(item.id)
                                    }
                                  >
                                    ລຶບ
                                  </Button>
                                </div>
                              ) : !isEditing ? (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-8 shrink-0 text-xs"
                                  onClick={() => {
                                    setLineCouponEditingId(item.id);
                                    setLineEditPickerValue(
                                      lineCoupons[item.id]?.code ?? "",
                                    );
                                  }}
                                >
                                  ໃສ່ coupon
                                </Button>
                              ) : null}
                            </div>
                            {isEditing && (
                              <div className="mt-2 space-y-2">
                                <PaymentCouponCombobox
                                  key={item.id}
                                  branchId={branchId}
                                  customerId={effectiveCustomerId}
                                  orderSubtotalForList={
                                    item.lineSubtotal + (item.taxTotal ?? 0)
                                  }
                                  baseAmountValidate={
                                    item.lineSubtotal + (item.taxTotal ?? 0)
                                  }
                                  value={lineEditPickerValue}
                                  onPickResult={(r) => {
                                    if (!r) {
                                      setLineEditPickerValue("");
                                      return;
                                    }
                                    setLineCoupons((prev) => ({
                                      ...prev,
                                      [item.id]: r,
                                    }));
                                    setLineCouponEditingId(null);
                                    setLineEditPickerValue("");
                                    toast.success("ໂຄດສ່ວນຫຼຸດຖືກຕ້ອງ");
                                  }}
                                  placeholder="ຄົ້ນຫາ ຫຼື ເລືອກໂຄດ..."
                                  queryKeySuffix={`line-${item.id}`}
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-full text-xs"
                                  onClick={() => {
                                    setLineCouponEditingId(null);
                                    setLineEditPickerValue("");
                                  }}
                                >
                                  ຍົກເລີກ
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CollapsibleContent>
              </Collapsible>

              <Collapsible
                defaultOpen={false}
                className="border-border/60 pb-4"
              >
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="group flex w-full items-center justify-between rounded-md py-2 text-left font-medium text-sm hover:bg-muted/50"
                  >
                    <span>ໂຄດ Voucher (ບໍ່ບັງຄັບ)</span>
                    <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-2 pt-2">
                  <div className="flex gap-2">
                    <Input
                      id="voucher-code"
                      type="text"
                      placeholder="ປ້ອນໂຄດ voucher"
                      value={voucherCode}
                      onChange={(e) =>
                        setVoucherCode(e.target.value.toUpperCase())
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleValidateVoucher();
                        }
                      }}
                      disabled={validateVoucherMutation.isPending}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleValidateVoucher}
                      disabled={
                        validateVoucherMutation.isPending || !voucherCode.trim()
                      }
                    >
                      {validateVoucherMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "ກວດສອບ"
                      )}
                    </Button>
                  </div>
                  {appliedVouchers.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {appliedVouchers.map((voucher) => (
                        <div
                          key={voucher.code}
                          className="flex items-center justify-between rounded-md bg-muted p-2"
                        >
                          <div className="flex flex-col gap-1">
                            <span className="font-medium text-sm">
                              {voucher.code}
                            </span>
                            <span className="text-muted-foreground text-xs">
                              ຫຼຸດ: {voucher.value.toLocaleString("lo-LA")} ₭
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveVoucher(voucher.code)}
                          >
                            ລຶບ
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CollapsibleContent>
              </Collapsible>
            </div>

            {/* ขวา: วิธีจ่าย + จำนวน + สรุป */}
            <div className="flex min-h-0 flex-col gap-4">
              {isLoadingMethods ? (
                <div className="flex justify-center py-6">
                  <Loader />
                </div>
              ) : (
                <>
                  <div className="flex min-h-0 flex-col gap-2">
                    <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
                      <Label className="text-sm" id="payment-split-label">
                        ການຊຳລະ (ແຍກຫຼາຍຊ່ອງທາງໄດ້)
                      </Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-1"
                        onClick={addPaymentRow}
                        disabled={paymentRows.length >= MAX_PAYMENT_ROWS}
                      >
                        <Plus className="h-4 w-4" />
                        ເພີ່ມການຈ່າຍ
                      </Button>
                    </div>

                    {/* <div
                    className="max-h-[min(52vh,28rem)] min-h-0 overflow-x-hidden overflow-y-auto scroll-smooth rounded-lg border border-border/50 bg-muted/10 p-2 sm:max-h-[min(60vh,34rem)]"
                    role="group"
                    aria-labelledby="payment-split-label"
                  > */}
                    <div className="space-y-2">
                      {paymentRows.map((row, rowIndex) => (
                        <div
                          key={row.id}
                          className="space-y-1.5 rounded-md border border-border/60 bg-background/80 p-2 shadow-sm"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium text-muted-foreground text-xs">
                              ຊ່ອງທີ່ {rowIndex + 1}
                            </span>
                            {paymentRows.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 gap-1 px-2 text-destructive hover:text-destructive"
                                onClick={() => removePaymentRow(row.id)}
                                aria-label="ລຶບຊ່ອງນີ້"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          <div className="space-y-1">
                            <Label
                              className="text-xs"
                              id={`payment-method-label-${row.id}`}
                            >
                              ວິທີການຈ່າຍເງິນ
                            </Label>
                            <div
                              aria-labelledby={`payment-method-label-${row.id}`}
                              className="flex max-h-22 flex-wrap gap-1.5 overflow-y-auto pr-0.5 [-webkit-overflow-scrolling:touch]"
                              role="radiogroup"
                            >
                              {paymentMethods?.map((method) => {
                                const isSelected = row.methodId === method.id;
                                return (
                                  <label
                                    key={method.id}
                                    className={cn(
                                      "cursor-pointer rounded-md border-2 px-2.5 py-1.5 font-medium text-xs transition-colors",
                                      isSelected
                                        ? "border-primary bg-primary/10 text-primary"
                                        : "border-border bg-background hover:border-muted-foreground/50",
                                    )}
                                  >
                                    <input
                                      type="radio"
                                      name={`payment-method-${row.id}`}
                                      value={method.id}
                                      checked={isSelected}
                                      onChange={() =>
                                        setPaymentRows((rows) =>
                                          rows.map((r) =>
                                            r.id === row.id
                                              ? { ...r, methodId: method.id }
                                              : r,
                                          ),
                                        )
                                      }
                                      className="sr-only"
                                      aria-label={method.displayName}
                                    />
                                    <span>{method.displayName}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label
                              htmlFor={`amount-paid-${row.id}`}
                              className="text-xs"
                            >
                              ຈຳນວນເງິນ (gross)
                            </Label>
                            <Input
                              id={`amount-paid-${row.id}`}
                              type="number"
                              placeholder="0"
                              value={row.amount}
                              onChange={(e) => {
                                const v = e.target.value;
                                setPaymentRows((rows) =>
                                  rows.map((r) =>
                                    r.id === row.id ? { ...r, amount: v } : r,
                                  ),
                                );
                              }}
                              min={0}
                              step="1"
                              className="h-9 text-sm"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* </div> */}
                  </div>

                  <div className="shrink-0 space-y-2 border-border/60 border-t pt-4">
                    {couponDiscount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          ສ່ວນຫຼຸດ (ໂຄດສ່ວນຫຼຸດ):
                        </span>
                        <span className="font-medium text-green-600">
                          -{couponDiscount.toLocaleString("lo-LA")} ₭
                        </span>
                      </div>
                    )}
                    {totalVoucherDiscount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          ສ່ວນຫຼຸດ Voucher:
                        </span>
                        <span className="font-medium text-green-600">
                          -{totalVoucherDiscount.toLocaleString("lo-LA")} ₭
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">ລວມທັງໝົດ:</span>
                      <span className="font-medium">
                        {effectiveOrderTotal.toLocaleString("lo-LA")} ₭
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        ລວມຈ່າຍ (ທຸກຊ່ອງ):
                      </span>
                      <span className="font-medium">
                        {paidRounded > 0
                          ? paidRounded.toLocaleString("lo-LA")
                          : "0"}{" "}
                        ₭
                      </span>
                    </div>
                    {paymentRows.length > 1 && remainingDue > 0 && (
                      <p className="text-muted-foreground text-xs">
                        ຍອດຄົງເຫຼືອຕາມບິນ: {remainingDue.toLocaleString("lo-LA")} ₭
                        — ແບ່ງໃສ່ຊ່ອງຫຼືເພີ່ມຊ່ອງ
                      </p>
                    )}
                    {change > 0 && (
                      <div className="flex justify-between font-medium text-base text-green-600">
                        <span>ເງິນທອນ (ລວມ):</span>
                        <span>{change.toLocaleString("lo-LA")} ₭</span>
                      </div>
                    )}
                    {paidRounded > 0 && paidRounded < effectiveRounded && (
                      <p className="text-destructive text-xs">
                        ຈຳນວນເງິນຈ່າຍບໍ່ພຽງພໍ (ຄ້າງ{" "}
                        {remainingDue.toLocaleString("lo-LA")} ₭)
                      </p>
                    )}
                    {effectiveRounded <= 0 && (
                      <p className="text-muted-foreground text-xs">
                        ຍອດຫຼັງຫັກສ່ວນຫຼຸດ 0 ₭ — ປ້ອນ 0 ຫຼືວ່າງໄວ້ ແລະ ເລືອກວິທີຊຳລະ
                        ແລ້ວກົດສຳເລັດ
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-0 shrink-0 border-border/60 border-t pt-4">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            ຍົກເລີກ
          </Button>
          <Button
            onClick={() => void handleComplete()}
            disabled={!canComplete || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ກຳລັງຈ່າຍ...
              </>
            ) : (
              "ສຳເລັດ"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
